/**
 * The VFX director (story `q-demo-vfx-pass`): subscribes to the game-feedback bus, maps
 * each event to bursts (`cues.ts`), spawns them into the fixed pool (`pool.ts`) and draws
 * the pool (`renderer.ts`). Owns the DOM damage vignette (the only non-particle effect).
 *
 * Budget (per render tier, `VFX_BUDGET`): a hard particle cap — a burst past it is
 * dropped, never queued — and at most one draw call per layer (four), zero while idle.
 * The player's Full / Reduced / Off setting (`settings.ts`) scales every burst.
 */

import type * as THREE from 'three';
import type { RenderTier } from '../scene/budget.js';
import type { FeedbackBus, GameFeedbackEvent } from '../feedback/feedbackBus.js';
import { cueSpawns, screenCue, type VfxAnchor } from './cues.js';
import { ParticlePool } from './pool.js';
import { VFX_PRESETS } from './presets.js';
import { VfxRenderer, vfxTextureBytes } from './renderer.js';
import { countScaleFor, vfxLevel, type VfxLevel } from './settings.js';

/** Live-particle cap per render tier. Presentation budget, not gameplay tuning. */
export const VFX_BUDGET: Readonly<Record<RenderTier, { particles: number; maxDrawCalls: number }>> =
  {
    desktop: { particles: 480, maxDrawCalls: 4 },
    mobile: { particles: 160, maxDrawCalls: 4 },
  };

/** Seconds between Dodge-trail afterimages. */
export const DODGE_TRAIL_INTERVAL_S = 0.035;

export interface WorldPoint {
  x: number;
  y: number;
  z: number;
}

export interface VfxDirectorOptions {
  scene: THREE.Object3D;
  tier: RenderTier;
  /** Feet position of an anchor, or null when it is not drawn (out of view, gone). */
  resolve: (anchor: VfxAnchor) => WorldPoint | null;
  bus?: FeedbackBus;
  /** Element the damage vignette overlays (the canvas's parent); null = none. */
  overlayHost?: HTMLElement | null;
  rand?: () => number;
  /** Override the stored player setting (tests). */
  level?: () => VfxLevel;
}

export class VfxDirector {
  readonly pool: ParticlePool;
  readonly renderer: VfxRenderer;
  private readonly resolve: VfxDirectorOptions['resolve'];
  private readonly level: () => VfxLevel;
  private readonly unsubscribe: (() => void) | null;
  private readonly vignette: HTMLDivElement | null;
  private vignetteTimer: ReturnType<typeof setTimeout> | null = null;
  /** performance-clock ms of the last vignette flash (a gallery freeze holds it on). */
  private vignetteAt = -Infinity;
  private trailClock = 0;
  /** Bursts asked for / spawned since construction (the DEV dump). */
  cues = 0;
  readonly tier: RenderTier;
  private peakDraws = 0;
  private peakCoverage = 0;

  constructor(opts: VfxDirectorOptions) {
    this.tier = opts.tier;
    const cap = VFX_BUDGET[opts.tier].particles;
    this.pool = new ParticlePool(cap, opts.rand);
    this.renderer = new VfxRenderer(cap);
    opts.scene.add(this.renderer.group);
    this.resolve = opts.resolve;
    this.level = opts.level ?? (() => vfxLevel());
    this.unsubscribe = opts.bus ? opts.bus.subscribe((ev) => this.handle(ev)) : null;
    this.vignette = makeVignette(opts.overlayHost ?? null);
  }

  /** Play one feedback event. */
  handle(event: GameFeedbackEvent): void {
    const level = this.level();
    const scale = countScaleFor(level);
    if (scale === 0) return;
    this.cues += 1;
    for (const spawn of cueSpawns(event)) {
      const at = this.resolve(spawn.anchor);
      if (at === null) continue;
      this.pool.burst(VFX_PRESETS[spawn.preset], at.x, at.y, at.z, spawn.scale ?? 1, scale);
    }
    if (level === 'full' && screenCue(event) === 'hurt') this.flashVignette();
  }

  /** The own avatar is mid-Dodge at (x, y, z): leave afterimages along the path. */
  trail(x: number, y: number, z: number, dt: number): void {
    const scale = countScaleFor(this.level());
    if (scale === 0) return;
    this.trailClock += dt;
    const interval = DODGE_TRAIL_INTERVAL_S / scale;
    if (this.trailClock < interval) return;
    this.trailClock = 0;
    this.pool.burst(VFX_PRESETS.dodgeTrail, x, y, z, 1, 1);
  }

  /** One Dodge afterimage at (x, y, z), off the trail clock (the DEV gallery). */
  afterimage(x: number, y: number, z: number): void {
    if (countScaleFor(this.level()) === 0) return;
    this.pool.burst(VFX_PRESETS.dodgeTrail, x, y, z, 1, 1);
  }

  /** Advance and draw. Allocation-free. */
  frame(dt: number, camera: THREE.Camera, widthPx: number, heightPx: number): void {
    this.pool.update(dt);
    this.renderer.sync(this.pool, camera, widthPx, heightPx);
    const s = this.renderer.stats;
    if (s.drawCalls > this.peakDraws) this.peakDraws = s.drawCalls;
    if (s.coverage > this.peakCoverage) this.peakCoverage = s.coverage;
  }

  /** DEV: slow (`?vfxhold=1`) or freeze (the gallery, 1) every effect so a lane still catches it. */
  setHold(hold: number): void {
    this.pool.hold = Math.min(1, Math.max(0, hold));
    // A freeze right after a damage flash keeps the vignette up, so a still can show it.
    const v = this.vignette;
    if (v !== null && this.pool.hold >= 1 && nowMs() - this.vignetteAt < 1000) {
      if (this.vignetteTimer !== null) clearTimeout(this.vignetteTimer);
      this.vignetteTimer = null;
      v.style.transition = 'none';
      v.style.opacity = '1';
    }
  }

  dump(): Record<string, unknown> {
    const s = this.renderer.stats;
    return {
      tier: this.tier,
      level: this.level(),
      budget: VFX_BUDGET[this.tier],
      live: this.pool.count,
      peak: this.pool.peak,
      dropped: this.pool.dropped,
      cues: this.cues,
      drawCalls: s.drawCalls,
      peakDrawCalls: this.peakDraws,
      coverage: Number(s.coverage.toFixed(4)),
      peakCoverage: Number(this.peakCoverage.toFixed(4)),
      textureBytes: vfxTextureBytes(),
      perLayer: Array.from(this.pool.perLayer),
    };
  }

  dispose(): void {
    this.unsubscribe?.();
    if (this.vignetteTimer !== null) clearTimeout(this.vignetteTimer);
    this.vignette?.remove();
    this.renderer.dispose();
    this.pool.clear();
  }

  private flashVignette(): void {
    const v = this.vignette;
    if (v === null) return;
    this.vignetteAt = nowMs();
    v.style.transition = 'none';
    v.style.opacity = '1';
    if (this.vignetteTimer !== null) clearTimeout(this.vignetteTimer);
    this.vignetteTimer = setTimeout(() => {
      v.style.transition = 'opacity 380ms ease-out';
      v.style.opacity = '0';
      this.vignetteTimer = null;
    }, 70);
  }
}

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function makeVignette(host: HTMLElement | null): HTMLDivElement | null {
  if (host === null || typeof document === 'undefined') return null;
  const v = document.createElement('div');
  v.className = 'gc-vfx-hurt';
  v.setAttribute('aria-hidden', 'true');
  v.dataset.testid = 'vfx-hurt-vignette';
  Object.assign(v.style, {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    opacity: '0',
    background:
      'radial-gradient(ellipse at center, rgba(0,0,0,0) 55%, rgba(200,40,30,0.38) 85%, rgba(150,20,15,0.6) 100%)',
    zIndex: '1',
  } satisfies Partial<CSSStyleDeclaration>);
  host.appendChild(v);
  return v;
}
